import { localDevRuntimeBypass } from "./dev-bypass"

export type AuthFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
const ACCESS_TOKEN_HEADER = "x-openanalyst-access-token"
const PROVIDER_TOKEN_HEADER = "x-openanalyst-provider-token"

export class AuthRequiredError extends Error {
  constructor(message = "Authentication required") {
    super(message)
    this.name = "AuthRequiredError"
  }
}

export function isAuthRequiredError(error: unknown): error is AuthRequiredError {
  return error instanceof AuthRequiredError || (error instanceof Error && error.name === "AuthRequiredError")
}

type AuthFetchDeps = {
  fetchImpl: AuthFetch
  getAccessToken: () => string | null
  getForwardedAccessToken?: () => string | null
  refreshAccessToken: () => Promise<string | null>
  shouldRefreshMissingForwardedToken?: (input: RequestInfo | URL, init?: RequestInit) => boolean
  allowLocalDevRuntimeFetch?: () => boolean
  now?: () => number
  missingForwardedTokenRefreshCooldownMs?: number
}

export const MISSING_FORWARDED_TOKEN_REFRESH_COOLDOWN_MS = 30_000

function withAuthorization(headersInit: HeadersInit | undefined, token: string | null, forwardedToken: string | null) {
  const headers = new Headers(headersInit)
  if (!token) return headers
  if (forwardedToken) headers.set(PROVIDER_TOKEN_HEADER, forwardedToken)
  if (headers.has("Authorization")) {
    headers.set(ACCESS_TOKEN_HEADER, token)
    return headers
  }
  headers.set("Authorization", `Bearer ${token}`)
  return headers
}

function hasAuthorization(input: RequestInfo | URL, init?: RequestInit) {
  if (input instanceof Request) return input.headers.has("Authorization")
  return new Headers(init?.headers).has("Authorization")
}

function isLoopbackRequest(input: RequestInfo | URL) {
  try {
    const url = new URL(input instanceof Request ? input.url : input instanceof URL ? input.href : String(input))
    return url.protocol === "http:" && ["localhost", "127.0.0.1", "::1", "[::1]"].includes(url.hostname)
  } catch {
    return false
  }
}

export function createAuthFetch(deps: AuthFetchDeps): AuthFetch {
  let nextMissingForwardedTokenRefreshAt = 0
  const execute = (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    token: string | null,
    forwardedToken: string | null,
  ) => {
    if (input instanceof Request) {
      const cloned = input.clone()
      return deps.fetchImpl(
        new Request(cloned, {
          headers: withAuthorization(cloned.headers, token, forwardedToken),
        }),
      )
    }
    return deps.fetchImpl(input, {
      ...init,
      headers: withAuthorization(init?.headers, token, forwardedToken),
    })
  }

  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const authorized = hasAuthorization(input, init)
    let token = deps.getAccessToken()
    let forwardedToken = deps.getForwardedAccessToken?.() ?? null

    if (
      !token &&
      !authorized &&
      (deps.allowLocalDevRuntimeFetch?.() ?? localDevRuntimeBypass()) &&
      isLoopbackRequest(input)
    ) {
      return execute(input, init, null, forwardedToken)
    }

    const now = deps.now?.() ?? Date.now()

    if (
      token &&
      deps.getForwardedAccessToken &&
      !forwardedToken &&
      deps.shouldRefreshMissingForwardedToken?.(input, init) &&
      now >= nextMissingForwardedTokenRefreshAt
    ) {
      nextMissingForwardedTokenRefreshAt =
        now + (deps.missingForwardedTokenRefreshCooldownMs ?? MISSING_FORWARDED_TOKEN_REFRESH_COOLDOWN_MS)
      const refreshed = await deps.refreshAccessToken()
      token = deps.getAccessToken() ?? refreshed ?? token
      forwardedToken = deps.getForwardedAccessToken() ?? forwardedToken
    }

    if (!token && authorized) {
      const response = await execute(input, init, null, forwardedToken)
      if (response.status !== 401) return response

      const refreshed = await deps.refreshAccessToken()
      if (!refreshed) return response

      return execute(input, init, refreshed, deps.getForwardedAccessToken?.() ?? forwardedToken)
    }

    if (!token) {
      token = await deps.refreshAccessToken()
      forwardedToken = deps.getForwardedAccessToken?.() ?? forwardedToken
    }

    if (!token && !authorized) {
      throw new AuthRequiredError()
    }

    const response = await execute(input, init, token, forwardedToken)
    if (response.status !== 401) return response

    const refreshed = await deps.refreshAccessToken()
    if (!refreshed) return response

    return execute(input, init, refreshed, deps.getForwardedAccessToken?.() ?? forwardedToken)
  }
}
